import React, { useState } from 'react';
import api from '../../utils/axios';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Shuffle, FileUp, Building2 } from 'lucide-react';
import { CvuToolPreview } from './CvuToolPreview';
import { AdvertenciaBadge } from './PreviewTabla';
import { BancosSponsorEditor } from './BancosSponsorEditor';
import { RevisionReemplazos } from './RevisionReemplazos';
import { ReporteUsuariosTool } from './ReporteUsuariosTool';

const mono = (v) => <span className="font-mono">{v || '—'}</span>;
const cliente = (f) => (
  <div><p className="font-medium text-slate-800">{f.razon_social || '—'}</p><p className="text-slate-400 font-mono">{f.cuit || ''}</p></div>
);
const cvuConBanco = (cvu, banco) => <div>{mono(cvu)}{banco && <p className="text-slate-400">{banco}</p>}</div>;
const listaCvus = (l) => (l || []).map((c) => (
  <p key={c.cvu} className="font-mono">{c.cvu} <span className="text-slate-400 font-sans">· {c.banco}{c.activo ? ' · activo' : ''}</span></p>
));

const ACCION_LABEL = {
  vincular: ['Vincular', 'bg-blue-100 text-blue-800'],
  agregar_y_vincular: ['Agregar y vincular', 'bg-indigo-100 text-indigo-800'],
  sin_cambios: ['Sin cambios', 'bg-slate-100 text-slate-600'],
  revision_manual: ['Revisión manual', 'bg-amber-100 text-amber-800'],
  error: ['Error', 'bg-rose-100 text-rose-700'],
};

const SUGERIR_TOTALES = [
  { key: 'clientes_revisados', label: 'Clientes revisados' },
  { key: 'sugerencias_automaticas', label: 'Reemplazos sugeridos', tono: 'blue' },
  { key: 'con_advertencia_anterior_activo', label: 'Anterior sigue activo', tono: 'amber' },
  { key: 'revision_manual', label: 'A revisión manual', tono: 'rose' },
  { key: 'aplicados', label: 'Aplicados', tono: 'emerald' },
];
const SUGERIR_TABLAS = [
  { key: 'filas', titulo: 'Reemplazos sugeridos', columnas: [
    { key: 'cliente', label: 'Cliente', render: cliente },
    { key: 'cvu_nuevo', label: 'CVU nuevo', render: (f) => cvuConBanco(f.cvu_nuevo, f.banco_nuevo) },
    { key: 'reemplaza_a', label: 'Reemplaza a', render: (f) => cvuConBanco(f.reemplaza_a, f.banco_anterior) },
    { key: 'motivo', label: 'Motivo' },
    { key: 'advertencia', label: 'Advertencia', render: (f) => <AdvertenciaBadge texto={f.advertencia} /> },
  ] },
  { key: 'revision', titulo: 'Van a revisión manual (no se aplican)', columnas: [
    { key: 'cliente', label: 'Cliente', render: cliente },
    { key: 'motivo', label: 'Motivo' },
    { key: 'cvus_anteriores', label: 'CVUs sponsor anterior', render: (f) => listaCvus(f.cvus_anteriores) },
    { key: 'cvus_nuevos', label: 'CVUs sponsor activo', render: (f) => listaCvus(f.cvus_nuevos) },
  ] },
];

const IMPORTAR_TOTALES = [
  { key: 'filas', label: 'Filas' },
  { key: 'vincular', label: 'A vincular', tono: 'blue' },
  { key: 'agregar_y_vincular', label: 'Agregar y vincular', tono: 'blue' },
  { key: 'con_advertencia_anterior_activo', label: 'Anterior sigue activo', tono: 'amber' },
  { key: 'revision_manual', label: 'Revisión manual', tono: 'rose' },
  { key: 'errores', label: 'Errores', tono: 'rose' },
  { key: 'sin_cambios', label: 'Sin cambios' },
  { key: 'aplicados', label: 'Aplicados', tono: 'emerald' },
];
const IMPORTAR_TABLAS = [
  { key: 'filas', titulo: 'Detalle por fila', columnas: [
    { key: 'fila', label: 'Fila' },
    { key: 'cvu_anterior', label: 'CVU anterior', render: (f) => cvuConBanco(f.cvu_anterior, f.banco_anterior) },
    { key: 'cvu_nuevo', label: 'CVU nuevo', render: (f) => cvuConBanco(f.cvu_nuevo, f.banco_nuevo) },
    { key: 'cliente', label: 'Cliente (del padrón)', render: cliente },
    { key: 'accion', label: 'Acción', render: (f) => {
      const [l, c] = ACCION_LABEL[f.accion] || [f.accion, ''];
      return <Badge className={`text-[10px] ${c}`}>{l}</Badge>;
    } },
    { key: 'detalle', label: 'Detalle' },
    { key: 'advertencia', label: 'Advertencia', render: (f) => <AdvertenciaBadge texto={f.advertencia} /> },
  ] },
];

const SYNC_TOTALES = [
  { key: 'empresas_con_cvus', label: 'Empresas con CVUs' },
  { key: 'cvus_a_agregar', label: 'CVUs a agregar al padrón', tono: 'blue' },
  { key: 'ya_estaban_en_padron', label: 'Ya estaban' },
  { key: 'empresas_sin_cuit_en_padron', label: 'Empresa sin CUIT en padrón', tono: 'amber' },
  { key: 'aplicados', label: 'Aplicados', tono: 'emerald' },
];
const SYNC_TABLAS = [
  { key: 'filas', titulo: 'CVUs que se agregan al padrón', columnas: [
    { key: 'cliente', label: 'Empresa', render: cliente },
    { key: 'cvu', label: 'CVU', render: (f) => mono(f.cvu) },
    { key: 'alias', label: 'Alias' },
    { key: 'activo', label: 'Estado', render: (f) => (f.activo ? 'Activo' : 'Inactivo') },
  ] },
  { key: 'sin_padron', titulo: 'Empresas cuyo CUIT no está en el padrón (no se tocan)', columnas: [
    { key: 'cliente', label: 'Empresa', render: cliente },
    { key: 'cvus', label: 'CVUs' },
  ] },
];

const postar = async (url) => (await api.post(url, null, { timeout: 180000 })).data;

export const HerramientasCvu = () => {
  const [archivo, setArchivo] = useState(null);

  const importar = async (ejecutar) => {
    const fd = new FormData();
    fd.append('file', archivo);
    const res = await api.post(`/padron-interno/importar-reemplazos?ejecutar=${ejecutar}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000,
    });
    return res.data;
  };

  return (
    <div className="space-y-4" data-testid="herramientas-cvu">
      <div>
        <h2 className="text-lg font-bold text-slate-900">Herramientas de CVU</h2>
        <p className="text-sm text-slate-500">
          Cada herramienta muestra primero una vista previa (no guarda nada). Los cambios se aplican solo al confirmar,
          son idempotentes y quedan registrados. El estado del CVU anterior nunca se modifica.
        </p>
      </div>
      <BancosSponsorEditor />
      <ReporteUsuariosTool />
      <CvuToolPreview
        testid="tool-sugerir" icono={Shuffle} titulo="Sugerir reemplazos"
        descripcion="Por cada CUIT busca un CVU de sponsor inactivo y uno de sponsor activo. Si hay un solo candidato de cada lado se sugiere el vínculo; si hay varios va a revisión manual."
        run={(e) => postar(`/padron-interno/sugerir-reemplazos?ejecutar=${e}`)}
        totalesDefs={SUGERIR_TOTALES} tablas={SUGERIR_TABLAS}
        puedeAplicar={(d) => (d.totales?.sugerencias_automaticas || 0) + (d.totales?.revision_manual_nuevas || 0) > 0}
        textoConfirmacion="Se guardará 'reemplaza_a' y 'motivo_cambio' en cada CVU nuevo sugerido, y los casos ambiguos quedarán en la lista de revisión manual."
      />
      <CvuToolPreview
        testid="tool-importar" icono={FileUp} titulo="Importar reemplazos"
        descripcion="Archivo CSV o Excel con columnas: CVU anterior, CVU nuevo, motivo. El CUIT se toma del padrón; si no coincide, la fila va a revisión manual."
        run={importar} disabled={!archivo} resetKey={archivo}
        totalesDefs={IMPORTAR_TOTALES} tablas={IMPORTAR_TABLAS}
        puedeAplicar={(d) => (d.totales?.vincular || 0) + (d.totales?.agregar_y_vincular || 0) + (d.totales?.revision_manual_nuevas || 0) > 0}
        textoConfirmacion="Se vincularán las filas marcadas 'Vincular' / 'Agregar y vincular'. Las de revisión manual quedarán pendientes."
      >
        <Input type="file" accept=".csv,.txt,.xlsx,.xls" className="max-w-sm h-9 text-sm"
          onChange={(e) => setArchivo(e.target.files?.[0] || null)} data-testid="tool-importar-file" />
      </CvuToolPreview>
      <CvuToolPreview
        testid="tool-sync-empresas" icono={Building2} titulo="Sincronizar CVUs de empresas al padrón"
        descripcion="Agrega al padrón los CVUs cargados en Empresas que todavía no figuran en el CUIT correspondiente."
        run={(e) => postar(`/empresas/sincronizar-cvus-padron?ejecutar=${e}`)}
        totalesDefs={SYNC_TOTALES} tablas={SYNC_TABLAS}
        puedeAplicar={(d) => (d.totales?.cvus_a_agregar || 0) > 0}
        textoConfirmacion="Se agregarán al padrón los CVUs listados (origen: Empresa)."
      />
      <RevisionReemplazos />
    </div>
  );
};
